import { useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSocialAccounts } from '../hooks/useSocialAccounts';
import { useSocialPosts } from '../hooks/useSocialPosts';
import { useSocialPostStats } from '../hooks/useSocialPostStats';
import { PublishingCalendar } from '../components/PublishingCalendar';
import { SocialMediaPost, SocialMediaDailyCount, SocialPostStatus } from '@/lib/api';
import { socialMediaApi } from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useHolderSwitcher } from '@/components/HolderSwitcher';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** Mon-first column headers for the mini calendar */
const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

/** Tailwind dot classes per post status, matching the badge palette in PostsTable */
const STATUS_DOT_CLASS: Record<SocialPostStatus, string> = {
  published: 'bg-teal-500',
  scheduled: 'bg-blue-500',
  draft: 'bg-gray-400',
  failed: 'bg-red-500',
  publishing: 'bg-yellow-500',
  cancelled: 'bg-gray-300',
};

// ── Date helpers (self-contained, no external deps) ───────────────────────────

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function toMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function startOfMonthFn(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonthsFn(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

function isSameDayFn(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isSameMonthFn(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function daysInMonthFn(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

// ── StatCard ──────────────────────────────────────────────────────────────────

interface StatCardProps {
  value: number;
  label: string;
}

function StatCard({ value, label }: StatCardProps) {
  return (
    <div className={`bg-card rounded-lg p-3 shadow-social-lighter`}>
      <p className="text-2xl font-bold leading-none text-card-foreground !font-satoshi">{value}</p>
      <p className="text-xs text-muted-foreground mt-1 !font-mono">{label}</p>
    </div>
  );
}

function MobileStatCell({ value, label }: StatCardProps) {
  return (
    <div className="flex flex-col items-center justify-center py-3 px-1 border-r border-border last:border-r-0 bg-card">
      <p className="text-lg font-bold leading-none text-card-foreground !font-satoshi">{value}</p>
      <p className="text-[9px] text-muted-foreground mt-1 !font-mono text-center leading-tight">
        {label}
      </p>
    </div>
  );
}

// ── SocialMiniCalendar ────────────────────────────────────────────────────────

interface SocialMiniCalendarProps {
  /** All posts available in the current page load; used to render status dots. */
  posts: SocialMediaPost[];
  /** The month currently shown — controlled by parent. */
  viewMonth: Date;
  /** Called when the user clicks a chevron to change month. */
  onViewMonthChange: (month: Date) => void;
}

function SocialMiniCalendar({ posts, viewMonth, onViewMonthChange }: SocialMiniCalendarProps) {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(today);

  /** Map of YYYY-MM-DD → posts on that day, keyed by publishAt or createdAt. */
  const postsByDate = useMemo(() => {
    const map = new Map<string, SocialMediaPost[]>();
    for (const post of posts) {
      const rawDate = post.publishAt ?? post.createdAt;
      if (!rawDate) continue;
      const key = toDateKey(new Date(rawDate));
      const existing = map.get(key) ?? [];
      map.set(key, [...existing, post]);
    }
    return map;
  }, [posts]);

  /**
   * 42-cell grid (6 rows × 7 cols), week starts Monday.
   * Leading / trailing cells come from adjacent months.
   */
  const cells = useMemo(() => {
    const result: { date: Date; inMonth: boolean }[] = [];
    const firstDay = startOfMonthFn(viewMonth);
    // Convert getDay() (0 = Sun) to Mon-first index (0 = Mon … 6 = Sun)
    const leadingDays = (firstDay.getDay() + 6) % 7;
    const totalDays = daysInMonthFn(viewMonth);
    const prevMonthLastDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 0).getDate();

    for (let i = leadingDays - 1; i >= 0; i--) {
      result.push({
        date: new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, prevMonthLastDay - i),
        inMonth: false,
      });
    }
    for (let d = 1; d <= totalDays; d++) {
      result.push({
        date: new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d),
        inMonth: true,
      });
    }
    let nextDay = 1;
    while (result.length < 42) {
      result.push({
        date: new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, nextDay++),
        inMonth: false,
      });
    }
    return result;
  }, [viewMonth]);

  return (
    <div className="select-none">
      {/* Month navigation — synced with parent; chevrons call onViewMonthChange */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => onViewMonthChange(addMonthsFn(viewMonth, -1))}
          className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs font-semibold !font-satoshi">
          {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
        </span>
        <button
          onClick={() => onViewMonthChange(addMonthsFn(viewMonth, 1))}
          className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Day-of-week column headers (Mo … Su) */}
      <div className="grid grid-cols-7 mb-0.5">
        {DAY_HEADERS.map((h) => (
          <div
            key={h}
            className="text-center text-[10px] text-muted-foreground font-medium py-0.5 !font-mono"
          >
            {h}
          </div>
        ))}
      </div>

      {/* Date grid */}
      <div className="grid grid-cols-7">
        {cells.map(({ date, inMonth }, idx) => {
          const key = toDateKey(date);
          const dayPosts = postsByDate.get(key) ?? [];
          const isToday = isSameDayFn(date, today);
          const isSelected =
            isSameDayFn(date, selectedDate) && isSameMonthFn(selectedDate, viewMonth);

          return (
            <button
              key={idx}
              onClick={() => setSelectedDate(date)}
              className="flex flex-col items-center py-0.5"
            >
              <span
                className={[
                  'w-6 h-6 flex items-center justify-center text-[11px] rounded-full transition-colors',
                  !inMonth ? 'text-muted-foreground/30' : '',
                  isSelected
                    ? 'bg-primary text-primary-foreground font-semibold'
                    : isToday
                      ? 'bg-primary/15 text-primary font-semibold'
                      : inMonth
                        ? 'hover:bg-muted text-foreground'
                        : '',
                ].join(' ')}
              >
                {date.getDate()}
              </span>

              {/* Status dots — up to 2, then +N overflow */}
              {inMonth && dayPosts.length > 0 && (
                <div className="flex items-center gap-px mt-0.5">
                  {dayPosts.slice(0, 2).map((post, i) => (
                    <span
                      key={i}
                      className={`w-1 h-1 rounded-full shrink-0 ${STATUS_DOT_CLASS[post.status]}`}
                    />
                  ))}
                  {dayPosts.length > 2 && (
                    <span className="text-[8px] text-muted-foreground leading-none ml-0.5">
                      +{dayPosts.length - 2}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── PostsAreaChart ─────────────────────────────────────────────────────────────

interface PostsAreaChartProps {
  /** Per-day activity counts for the viewed month from the stats endpoint. */
  dailyActivity: SocialMediaDailyCount[];
  /** The month being charted — used to build the full day list. */
  viewMonth: Date;
}

function PostsAreaChart({ dailyActivity, viewMonth }: PostsAreaChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  /** Build a full day-by-day array for the month, filling in zeros. */
  const dayData = useMemo(() => {
    const activityMap = new Map(dailyActivity.map((d) => [d.date, d.count]));
    const total = daysInMonthFn(viewMonth);
    return Array.from({ length: total }, (_, i) => {
      const date = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i + 1);
      return { date, count: activityMap.get(toDateKey(date)) ?? 0 };
    });
  }, [dailyActivity, viewMonth]);

  const counts = dayData.map((d) => d.count);
  const maxCount = Math.max(...counts, 1);
  const peakIdx = counts.indexOf(Math.max(...counts));

  // SVG viewport — taller than before for readability
  const VW = 260;
  const VH = 110;
  const PAD = { top: 10, right: 10, bottom: 24, left: 10 };
  const plotW = VW - PAD.left - PAD.right;
  const plotH = VH - PAD.top - PAD.bottom;

  const n = dayData.length;
  const step = n > 1 ? plotW / (n - 1) : plotW;

  const toX = (i: number) => PAD.left + i * step;
  const toY = (count: number) => PAD.top + plotH - (count / maxCount) * plotH;

  const pts = counts.map((c, i) => ({ x: toX(i), y: toY(c) }));

  /** Smooth cubic bezier through all points */
  function buildPath(points: { x: number; y: number }[]): string {
    if (points.length < 2) return `M ${points[0]?.x ?? 0} ${points[0]?.y ?? PAD.top}`;
    const parts: string[] = [`M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`];
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const t = 0.35;
      const cp1x = prev.x + (curr.x - prev.x) * t;
      const cp2x = curr.x - (curr.x - prev.x) * t;
      parts.push(
        `C ${cp1x.toFixed(1)} ${prev.y.toFixed(1)},` +
          ` ${cp2x.toFixed(1)} ${curr.y.toFixed(1)},` +
          ` ${curr.x.toFixed(1)} ${curr.y.toFixed(1)}`,
      );
    }
    return parts.join(' ');
  }

  const linePath = buildPath(pts);
  const baselineY = (PAD.top + plotH).toFixed(1);
  const areaPath = [
    linePath,
    `L ${pts[pts.length - 1].x.toFixed(1)} ${baselineY}`,
    `L ${pts[0].x.toFixed(1)} ${baselineY}`,
    'Z',
  ].join(' ');

  /** X-axis labels: show day numbers at reasonable intervals */
  const xLabels = useMemo(() => {
    const interval = n <= 15 ? 1 : n <= 20 ? 2 : 5;
    return dayData
      .map((d, i) => ({ i, label: String(d.date.getDate()) }))
      .filter(({ i }) => i % interval === 0 || i === n - 1);
  }, [dayData, n]);

  const monthLabel = `${MONTH_NAMES[viewMonth.getMonth()].slice(0, 3)} ${viewMonth.getFullYear()}`;

  /**
   * Convert a mouse X position (relative to SVG element) to the nearest
   * day index in the chart.
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svgEl = e.currentTarget;
      const rect = svgEl.getBoundingClientRect();
      const rawX = ((e.clientX - rect.left) / rect.width) * VW;
      const plotX = rawX - PAD.left;
      const idx = Math.round((plotX / plotW) * (n - 1));
      setHoverIndex(Math.max(0, Math.min(n - 1, idx)));
    },
    [n, plotW],
  );

  const handleMouseLeave = useCallback(() => setHoverIndex(null), []);

  /** Tooltip position — clipped so it stays within the container */
  const tooltip = useMemo(() => {
    if (hoverIndex === null) return null;
    const pt = pts[hoverIndex];
    const day = dayData[hoverIndex];
    return { x: pt.x, y: pt.y, date: toDateKey(day.date), count: day.count };
  }, [hoverIndex, pts, dayData]);

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold !font-satoshi">Posts</span>
        <span className="text-xs text-muted-foreground !font-mono">{monthLabel}</span>
      </div>

      {/* Chart container — overflow:hidden clips the tooltip */}
      <div ref={containerRef} className="relative overflow-hidden rounded">
        <svg
          width="100%"
          viewBox={`0 0 ${VW} ${VH}`}
          className="block"
          aria-hidden="true"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            <linearGradient id="posts-area-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a5b4fc" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#a5b4fc" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Soft lavender fill */}
          <path d={areaPath} fill="url(#posts-area-gradient)" />

          {/* Smooth purple line */}
          <path
            d={linePath}
            fill="none"
            stroke="#818cf8"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Dashed vertical peak marker + dot */}
          {maxCount > 0 && (
            <>
              <line
                x1={pts[peakIdx].x}
                y1={PAD.top}
                x2={pts[peakIdx].x}
                y2={PAD.top + plotH}
                stroke="#818cf8"
                strokeWidth="1"
                strokeDasharray="3 2"
                opacity="0.55"
              />
              <circle
                cx={pts[peakIdx].x}
                cy={pts[peakIdx].y}
                r="3"
                fill="#818cf8"
                stroke="white"
                strokeWidth="1.5"
              />
            </>
          )}

          {/* Hover scrub indicator */}
          {hoverIndex !== null && tooltip && (
            <>
              <line
                x1={tooltip.x}
                y1={PAD.top}
                x2={tooltip.x}
                y2={PAD.top + plotH}
                stroke="#6366f1"
                strokeWidth="1"
                opacity="0.6"
              />
              <circle
                cx={tooltip.x}
                cy={tooltip.y}
                r="3.5"
                fill="#6366f1"
                stroke="white"
                strokeWidth="1.5"
              />
            </>
          )}

          {/* X-axis day labels */}
          {xLabels.map(({ i, label }) => (
            <text
              key={i}
              x={PAD.left + i * step}
              y={VH - 6}
              textAnchor="middle"
              fontSize="8"
              fill="#9ca3af"
            >
              {label}
            </text>
          ))}
        </svg>

        {/* Hover tooltip — absolutely positioned, clipped by parent overflow:hidden */}
        {hoverIndex !== null && tooltip && (
          <div
            className="pointer-events-none absolute z-10 px-2 py-1 rounded bg-popover border border-border shadow text-xs text-popover-foreground whitespace-nowrap"
            style={{
              // Position relative to the container width using percentage
              left: `clamp(0px, calc(${(tooltip.x / VW) * 100}% - 32px), calc(100% - 80px))`,
              top: 4,
            }}
          >
            <span className="font-medium">{tooltip.count}</span>
            <span className="text-muted-foreground ml-1">{tooltip.date.slice(5)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── DashboardPage ─────────────────────────────────────────────────────────────

type StatusTab = 'all' | SocialPostStatus;

export function DashboardPage() {
  const navigate = useNavigate();
  const { selectedHolderId } = useHolderSwitcher();
  const [activeTab, setActiveTab] = useState<StatusTab>('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const {
    accounts,
    isLoading: accountsLoading,
    disconnectAccount,
  } = useSocialAccounts(selectedHolderId);

  /** Shared month state — drives the mini calendar, chart, and stats fetch. */
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonthFn(new Date()));
  const monthKey = toMonthKey(viewMonth);

  const { counts } = useSocialPostStats(selectedHolderId, monthKey);

  // Fetch ALL posts (all statuses) for the visible mini-calendar month so the
  // dots and the activity chart reflect drafts, scheduled, published, and failed.
  const miniCalendarFrom = useMemo(() => {
    const d = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [viewMonth]);

  const miniCalendarTo = useMemo(() => {
    const d = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [viewMonth]);

  const miniCalendarPostsQuery = useQuery({
    queryKey: queryKeys.socialMedia.calendarPosts(
      selectedHolderId ?? '',
      miniCalendarFrom.toISOString(),
      miniCalendarTo.toISOString(),
    ),
    queryFn: () =>
      selectedHolderId
        ? socialMediaApi
            .listSocialMediaPosts(selectedHolderId, {
              from: miniCalendarFrom.toISOString(),
              to: miniCalendarTo.toISOString(),
              limit: 500,
            })
            .then((r) => r.posts)
        : Promise.resolve([]),
    enabled: !!selectedHolderId,
    staleTime: 2 * 60 * 1000,
  });

  const miniCalendarPosts = miniCalendarPostsQuery.data ?? [];

  // Derive per-day activity counts from ALL fetched posts (all statuses).
  // Uses COALESCE(publishAt, createdAt) as the date key — matching the backend ordering.
  const derivedDailyActivity = useMemo((): SocialMediaDailyCount[] => {
    const countMap = new Map<string, number>();
    for (const post of miniCalendarPosts) {
      const key = toDateKey(new Date(post.publishAt ?? post.createdAt));
      countMap.set(key, (countMap.get(key) ?? 0) + 1);
    }
    return Array.from(countMap.entries()).map(([date, count]) => ({ date, count }));
  }, [miniCalendarPosts]);

  const statusFilter = activeTab === 'all' ? undefined : activeTab;

  // Still fetch recent posts for the mini calendar status dots and the main calendar
  const {
    posts,
    total,
    isLoading,
    error,
    hasPrevPage,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
    lastItem,
    deletePost,
  } = useSocialPosts(selectedHolderId, statusFilter, debouncedSearch);

  // Preserved from original — used by error-account state tracking
  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const errorAccounts = accounts.filter((a) => a.status !== 'active');

  // Suppress unused-variable warnings; these are kept for future use
  void accountsLoading;
  void activeAccounts;
  void errorAccounts;
  void disconnectAccount;

  const handleCreatePost = () => navigate('/a/arche_social_media/compose');

  return (
    <div className="flex flex-col lg:flex-row h-full overflow-hidden bg-social-bg">
      {/* ── Mobile-only: stats strip + collapsible chart ── */}
      <div className="relative lg:hidden flex-shrink-0">
        {/* 4 stat cells flush, no gap, no rounded */}
        <div className="grid grid-cols-4 border-b border-border">
          <MobileStatCell value={counts.published} label="Published" />
          <MobileStatCell value={counts.total} label="Total" />
          <MobileStatCell value={counts.scheduled} label="Scheduled" />
          <MobileStatCell value={counts.failed} label="Failed" />
        </div>

        {/* Collapsible activity chart */}
        <div className="py-4">
          <div className="px-4 pb-3">
            <PostsAreaChart dailyActivity={derivedDailyActivity} viewMonth={viewMonth} />
          </div>
        </div>

        <div
          className="pointer-events-none absolute -bottom-7 left-0 right-0 h-8 z-10"
          style={{
            background:
              'linear-gradient(to bottom, var(--social-bg), rgba(var(--social-bg-rgb), 0))',
          }}
        />
      </div>

      {/* ── Desktop left sidebar ── */}
      <aside className="hidden lg:flex w-[310px] shrink-0 bg-muted/20 overflow-y-auto p-4 flex-col gap-5 no-scrollbar">
        {/* 1. Stats — 2×2 grid */}
        <div className="grid grid-cols-2 gap-2">
          <StatCard value={counts.published} label="Published" />
          <StatCard value={counts.total} label="Total Posts" />
          <StatCard value={counts.scheduled} label="Scheduled" />
          <StatCard value={counts.failed} label="Failed" />
        </div>

        {/* 2. Mini calendar */}
        <SocialMiniCalendar
          posts={miniCalendarPosts}
          viewMonth={viewMonth}
          onViewMonthChange={setViewMonth}
        />

        {/* 3. Monthly post-activity area chart */}
        <PostsAreaChart dailyActivity={derivedDailyActivity} viewMonth={viewMonth} />

        {PublishNowCard()}
      </aside>

      {/* ── Main content ── */}
      <main className="flex-1 min-w-0 overflow-auto py-3 lg:p-6 no-scrollbar">
        <PublishingCalendar
          posts={posts}
          onCreatePost={handleCreatePost}
          deletePost={deletePost}
          error={error}
          total={total}
          isLoading={isLoading}
          hasPrevPage={hasPrevPage}
          hasNextPage={hasNextPage}
          goToNextPage={goToNextPage}
          goToPrevPage={goToPrevPage}
          setActiveTab={setActiveTab}
          setDebouncedSearch={setDebouncedSearch}
          debouncedSearch={debouncedSearch}
          lastItem={lastItem}
          statusFilter={activeTab}
        />
      </main>
    </div>
  );
}

export default function PublishNowCard() {
  const navigate = useNavigate();

  return (
    <div className="max-w-md rounded-2xl p-6 bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg">
      <h2 className="text-lg font-satoshi font-semibold mb-1 ">Ready to publish?</h2>

      <p className="text-xs opacity-90 mb-6 font-satoshi">
        You haven’t created a post yet. Start writing and publish your first post now.
      </p>

      <button
        onClick={() => navigate('/compose')}
        className="w-full bg-background text-primary text-[12px] font-normal py-1 rounded-xl hover:bg-muted transition font-mono"
      >
        PUBLISH NOW
      </button>
    </div>
  );
}
